import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Pause, Play, Search, Star } from 'lucide-react'
import { api } from '../api'
import { Button, Field, Input, ErrorText, Skeleton, Badge } from './ui'
import { cn } from '../lib/utils'

// Picking an avatar's voice by pasting an ID meant leaving the app, finding
// the voice, copying a string, and discovering a mistake later as an
// authentication failure mid-job. This lists what the workspace's key can
// actually use -- cloned voices included -- and plays each one, because a
// voice is chosen by ear and not by name.

export type Voice = {
  voice_id: string
  name: string
  category: string
  description?: string
  preview_url?: string
  gender?: string
  accent?: string
  use_case?: string
  age?: string
  owned: boolean
}

export function VoicePicker({ ws, value, onChange }: {
  ws: string
  value: string
  onChange: (voiceID: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [manual, setManual] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)

  // Closes on a click anywhere outside and on Escape. Both are expected of
  // anything that looks like a select, and without them the panel covers the
  // fields below it with no obvious way back.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Typing is what someone does next with 64 voices, so the search takes focus
  // on open rather than making them click it.
  useEffect(() => { if (open) search.current?.focus() }, [open])

  // Opens upward when there is not room below. Voice is the last field on the
  // avatar form, so downward is exactly where the window ends -- the list
  // would open into nothing and have to be scrolled into view.
  const [up, setUp] = useState(false)
  useEffect(() => {
    if (!open) return
    const r = box.current?.getBoundingClientRect()
    if (!r) return
    const panel = 300 // search bar plus the list at its max height
    setUp(window.innerHeight - r.bottom < panel && r.top > panel)
  }, [open])

  const pick = (id: string) => {
    onChange(id)
    setOpen(false)
    // Cleared so the next open shows the whole list rather than the last
    // search, which reads as voices having gone missing.
    setQuery('')
  }

  const voices = useQuery({
    queryKey: ['voices', ws],
    queryFn: () => api.get<Voice[]>(`/api/workspaces/${ws}/voices`),
    // Voices change when someone clones one, which is rare. Long enough to
    // avoid refetching while filling in a form, short enough that a new clone
    // appears without a reload.
    staleTime: 5 * 60_000,
  })

  const selected = voices.data?.find(v => v.voice_id === value)

  const filtered = useMemo(() => {
    const list = voices.data ?? []
    const q = query.trim().toLowerCase()
    if (!q) return list
    return list.filter(v =>
      [v.name, v.accent, v.gender, v.use_case, v.description]
        .filter(Boolean).join(' ').toLowerCase().includes(q))
  }, [voices.data, query])

  // The key may lack permission, or not be set at all. Pasting an ID has to
  // stay possible, or a workspace in that state cannot create an avatar.
  if (voices.isError || manual) {
    return (
      <Field
        label="ElevenLabs voice ID"
        help={voices.isError
          ? 'Could not list voices with this workspace’s key, so enter the ID by hand.'
          : undefined}
      >
        <Input
          value={value} onChange={e => onChange(e.target.value)}
          placeholder="FGY2WhTYpPnrIDTdsKH5" className="font-mono text-xs"
        />
        {!voices.isError && (
          <Button variant="link" className="mt-1" onClick={() => setManual(false)}>
            Choose from the list instead
          </Button>
        )}
      </Field>
    )
  }

  if (voices.isLoading) return <Skeleton className="h-9.5 w-full max-w-sm" />

  const owned = filtered.filter(v => v.owned)
  const stock = filtered.filter(v => !v.owned)

  return (
    <div className="space-y-2">
      <div ref={box} className="relative max-w-sm">
        <div className="flex items-center gap-2">
          {/* The preview sits OUTSIDE the trigger rather than inside it: a
              button nested in a button is invalid, and hearing the current
              choice should not mean opening the list to reach the play
              control. */}
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
            className={cn(
              'flex h-9.5 min-w-0 flex-1 items-center justify-between gap-2 px-3',
              'rounded-[--radius-control] border border-line bg-surface-2 text-left text-sm',
              'transition-colors hover:border-line-strong',
              'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25',
            )}
          >
            {selected ? (
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate">{selected.name}</span>
                {selected.owned && <Star className="size-3 shrink-0 text-brand" />}
                <span className="truncate text-xs text-ink-faint">{describe(selected)}</span>
              </span>
            ) : (
              <span className="text-ink-faint">
                {value ? value : 'Choose a voice'}
              </span>
            )}
            <ChevronDown className={cn('size-4 shrink-0 opacity-60 transition-transform',
              open && 'rotate-180')} />
          </button>

          {selected?.preview_url && <PreviewButton url={selected.preview_url} />}
        </div>

        {open && (
          <div
            className={cn(
              'absolute left-0 right-0 z-30 overflow-hidden',
              up ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
              'rounded-[--radius-card] border border-line bg-surface-1',
              'shadow-[0_16px_48px_oklch(0_0_0/0.5)]',
            )}
          >
            <div className="relative border-b border-line">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-ink-faint" />
              <input
                ref={search}
                value={query} onChange={e => setQuery(e.target.value)}
                placeholder={`Search ${voices.data?.length ?? 0} voices`}
                className="h-10 w-full bg-transparent pl-9 pr-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none"
              />
            </div>

            <div className="max-h-64 space-y-1 overflow-y-auto p-1">
              {filtered.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-ink-faint">
                  No voice matches “{query}”.
                </p>
              )}

              {owned.length > 0 && <GroupLabel>Your voices</GroupLabel>}
              {owned.map(v => (
                <VoiceRow key={v.voice_id} voice={v} selected={v.voice_id === value}
                  onSelect={() => pick(v.voice_id)} />
              ))}

              {stock.length > 0 && owned.length > 0 && <GroupLabel>Stock voices</GroupLabel>}
              {stock.map(v => (
                <VoiceRow key={v.voice_id} voice={v} selected={v.voice_id === value}
                  onSelect={() => pick(v.voice_id)} />
              ))}
            </div>
          </div>
        )}
      </div>

      <Button variant="link" onClick={() => setManual(true)}>Enter an ID instead</Button>
      <ErrorText error={voices.error} />
    </div>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
      {children}
    </div>
  )
}

function VoiceRow({ voice, selected, onSelect }: {
  voice: Voice
  selected: boolean
  onSelect: () => void
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-[--radius-control] px-2 py-1.5 transition-colors',
        selected ? 'bg-brand/10' : 'hover:bg-surface-2',
      )}
    >
      {voice.preview_url ? <PreviewButton url={voice.preview_url} /> : <span className="size-8" />}

      <button onClick={onSelect} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-1.5">
          <span className={cn('truncate text-sm', selected && 'font-medium text-brand')}>
            {voice.name}
          </span>
          {voice.owned && <Star className="size-3 shrink-0 text-brand" />}
        </div>
        <div className="truncate text-[11px] text-ink-faint">{describe(voice)}</div>
      </button>

      {selected
        ? <Badge tone="brand">selected</Badge>
        : <Button variant="ghost" size="sm" onClick={onSelect}>Use</Button>}
    </div>
  )
}

/** One shared audio element, so starting a preview stops the one already
 *  playing. Two voices talking over each other tells you nothing about
 *  either. */
let current: HTMLAudioElement | null = null

function PreviewButton({ url }: { url: string }) {
  const [playing, setPlaying] = useState(false)
  const ref = useRef<HTMLAudioElement | null>(null)

  useEffect(() => () => { ref.current?.pause(); if (current === ref.current) current = null }, [])

  const toggle = () => {
    if (playing) {
      ref.current?.pause()
      setPlaying(false)
      return
    }
    current?.pause()
    const a = ref.current ?? new Audio(url)
    ref.current = a
    a.onended = () => setPlaying(false)
    a.onpause = () => setPlaying(false)
    current = a
    void a.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
  }

  return (
    <button
      onClick={toggle}
      aria-label={playing ? 'Stop preview' : 'Play preview'}
      className={cn(
        'grid size-8 shrink-0 place-items-center rounded-full transition-colors',
        playing ? 'bg-brand text-brand-ink' : 'bg-surface-3 text-ink-dim hover:text-ink',
      )}
    >
      {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5 translate-x-px" />}
    </button>
  )
}

function describe(v: Voice): string {
  const parts = [v.gender, v.age, v.accent, v.use_case?.replace(/_/g, ' ')].filter(Boolean)
  return parts.length ? parts.join(' · ') : v.description || v.category
}
